import clsx from 'clsx'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Image from 'components/Common/Image'
import type { LoyaltyLandingPageBenefitDetails } from '../LoyaltyLandingPageData'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'
import { getSearchQueryFromFilter } from 'lib/search/searchUtils'
import Places from 'constants/places'
import { LOUNGE_PASSES_PAGE, LOYALTY_REWARDS_PAGE } from 'loyalty/constants/loyaltyRoutes'

const LargeTile = styled(Pane)`
  display: grid;
  grid-template-rows: ${rem(148)} 1fr;
  grid-template-areas:
  'image'
  'content';

  ${mediaQueryUp.tablet} {
    grid-template-rows: auto;
    grid-template-columns: 1fr 1fr;
    grid-template-areas: 'image content';

    &.reverse {
      grid-template-areas: 'content image';
    }
  }
`

const LargeTileContent = styled(Group)`
  padding: ${rem(16)};
  grid-area: content;

  ${mediaQueryUp.tablet} {
    padding: ${rem(68)} ${rem(24)};
  }
`

const TileImageContainer = styled.div`
  position: relative;
  grid-area: image;
`

const TileImage = styled(Image)`
  position: absolute;
  inset-inline-start: 0;
  top: 0;
`

interface Props {
  tile: LoyaltyLandingPageBenefitDetails
  /**
   * Reverses the order of the content (content first, image second)
   */
  reverse?: boolean
  /**
   * Will show a button to activate free membership
   */
  onActivate?: () => void
  /**
   * Promotion for benefit
   */
  promotionLabel?: React.ReactElement
  /**
   * Tile surface colour. Defaults to white (Pane default); pass `'light-background'` for the
   * grey-card-on-white inverted sections.
   */
  background?: 'light-background' | 'normal-background'
}

const FILTERS: App.OfferListFilters = {
  destinations: [Places.Anywhere.id],
  loyalty: ['Free Room Upgrade'],
}

const OFFERS_LINK = `/search?${getSearchQueryFromFilter(FILTERS)}`

function LoyaltyLandingPageLargeTile(props: Props) {
  const intl = useIntl()
  const { tile, reverse, onActivate, promotionLabel, background } = props
  const userHasUpgradesAvailable = useUserHasLoyaltyBenefit('hotel_upgrade')

  const activateButtonText = intl.formatMessage({
    id: 'XsmacAnkvFTFPycNtrTo',
    defaultMessage: 'Activate your free membership',
    description: 'Button to activate free loyalty program membership on benefit tile',
  })
  const viewOffersButtonText = intl.formatMessage({
    id: 'blOc5O93tLkSbSIuSUiD',
    defaultMessage: 'View offers',
    description: 'Button to view available offers with room upgrade benefit',
  })
  const exploreLoungesButtonText = intl.formatMessage({
    id: '3S5FM24RDUY0IM-IrtGO',
    defaultMessage: 'Explore lounges',
    description: 'Button to explore available airport lounges on the loyalty landing page',
  })

  return <LargeTile className={clsx({ reverse })} background={background}>
    <TileImageContainer>
      <TileImage image={tile.image} fit="center" dpr={2} />
    </TileImageContainer>
    <LargeTileContent direction="vertical" horizontalAlign="start" gap={16} tabletGap={24}>
      <VerticalSpacer gap={12}>
        {promotionLabel}
        <Heading variant="heading3">{tile.title}</Heading>
        <BodyText variant="large">{tile.description}</BodyText>
      </VerticalSpacer>
      {!!onActivate && <TextButton kind="primary" onClick={onActivate}>
        {activateButtonText}
      </TextButton>}
      {tile.benefitCode === 'hotel_upgrade' && userHasUpgradesAvailable &&
        <TextButton
          kind="secondary"
          to={OFFERS_LINK}
        >
          {viewOffersButtonText}
        </TextButton>}
      {tile.benefitCode === 'lounge_pass' &&
        <TextButton
          kind="secondary"
          to={`/${LOUNGE_PASSES_PAGE}`}
        >
          {exploreLoungesButtonText}
        </TextButton>}
      {tile.benefitCode === 'societe_rewards' &&
        <TextButton
          kind="secondary"
          to={`/${LOYALTY_REWARDS_PAGE}`}
        >
          {intl.formatMessage({
            id: 'fphcbxTWZhqyjGlxqaU4',
            defaultMessage: 'Learn more',
            description: 'Button label on the rewards CTA card linking to the Société Rewards page',
          })}
        </TextButton>}
    </LargeTileContent>
  </LargeTile>
}

export default LoyaltyLandingPageLargeTile
